import dayjs from 'dayjs';

// ======================================================================
// @classroom-ai/pacing — the Pacing Map algorithm, shared by the teacher app
// (client-side `usePacing`) and the agent (server-side `computeClassPacing`,
// Ask AI epic #391). Pure and SDK-decoupled: callers pass plain shapes (the
// Data Connect row shapes fit structurally), nothing reads the clock.
//
// Deliberately ONE file — the agent's tsx runs can't resolve a workspace
// package's relative imports (same rule as packages/types).
// ======================================================================

// ----------------------------------------------------------------------
// Day enumeration: which real dates a class can be scheduled on. Shared by the
// admin School Year detail, the teacher Calendar, and pacing.
// ----------------------------------------------------------------------

/** A special-day range as consumed here. Only these three fields matter. */
export type SpecialDayRange = {
  /** Inclusive ISO date `YYYY-MM-DD`. */
  startDate: string;
  /** Inclusive ISO date `YYYY-MM-DD`. */
  endDate: string;
  /** Whether the range removes instructional days (true) or is informational only (false). */
  nonInstructional: boolean;
};

/** A per-date override assigning an alternate daily schedule. */
export type DateScheduleAssignment = {
  /** ISO date `YYYY-MM-DD`. */
  date: string;
  /** Id of the daily schedule that applies on `date`. */
  dailyScheduleId: string;
};

const ISO = 'YYYY-MM-DD';

/** True for Mon–Fri (dayjs day(): 0 = Sun, 6 = Sat). */
function isWeekday(d: dayjs.Dayjs): boolean {
  const dow = d.day();
  return dow !== 0 && dow !== 6;
}

/**
 * Collect the distinct Mon–Fri ISO dates covered by a special-day range,
 * clamped to `[boundStart, boundEnd]`, into `into`. Overlapping ranges
 * naturally dedupe because they write into a shared Set.
 */
function collectWeekdaysInRange(
  range: SpecialDayRange,
  boundStart: dayjs.Dayjs,
  boundEnd: dayjs.Dayjs,
  into: Set<string>
): void {
  let d = dayjs(range.startDate);
  if (d.isBefore(boundStart)) d = boundStart;
  let last = dayjs(range.endDate);
  if (last.isAfter(boundEnd)) last = boundEnd;
  while (!d.isAfter(last)) {
    if (isWeekday(d)) into.add(d.format(ISO));
    d = d.add(1, 'day');
  }
}

/**
 * (a) Year-wide instructional dates: the ordered, distinct Mon–Fri ISO dates in
 * the inclusive range `[yearStart, yearEnd]`, minus the **deduped** union of the
 * `nonInstructional` special-day ranges.
 *
 * Passing an empty `specialDays` yields every weekday in the year — equivalent
 * to the old `countWeekdays(yearStart, yearEnd)`. Passing the year's special
 * days yields the "Educational days" set. Overlapping non-instructional ranges
 * are counted once (the old per-range `reduce` double-counted them).
 */
export function listInstructionalDates(
  yearStart: string,
  yearEnd: string,
  specialDays: SpecialDayRange[]
): string[] {
  const start = dayjs(yearStart);
  const end = dayjs(yearEnd);

  const excluded = new Set<string>();
  for (const sd of specialDays) {
    if (sd.nonInstructional) collectWeekdaysInRange(sd, start, end, excluded);
  }

  const dates: string[] = [];
  let d = start;
  while (!d.isAfter(end)) {
    if (isWeekday(d)) {
      const iso = d.format(ISO);
      if (!excluded.has(iso)) dates.push(iso);
    }
    d = d.add(1, 'day');
  }
  return dates;
}

/**
 * (b) Narrow instructional dates to the days **this class actually meets**. For
 * each date, resolve the applicable daily schedule — the `ScheduleAssignment`
 * for that date if one exists, otherwise the school's `defaultScheduleId` — and
 * keep the date iff the class has a period on that schedule
 * (`classScheduleIds.has(resolvedScheduleId)`).
 *
 * Dates whose applicable schedule is unknown (no assignment and no default) are
 * dropped, as are alternate-schedule days the class has no period on.
 *
 * `instructionalDates` is assumed already ordered (as returned by
 * {@link listInstructionalDates}); order is preserved.
 */
export function listClassMeetingDates(
  instructionalDates: string[],
  assignments: DateScheduleAssignment[],
  defaultScheduleId: string | null,
  classScheduleIds: Set<string>
): string[] {
  const assignmentByDate = new Map<string, string>();
  for (const a of assignments) assignmentByDate.set(a.date, a.dailyScheduleId);

  return instructionalDates.filter((date) => {
    const scheduleId = assignmentByDate.get(date) ?? defaultScheduleId;
    return scheduleId != null && classScheduleIds.has(scheduleId);
  });
}

/** A meeting day on which no lesson may be placed, with the schedule that blocks it. */
export type NoLessonDay = { date: string; scheduleId: string };

/**
 * (c) Split a class's meeting dates into the days a lesson can be placed on and the
 * days it can't (epic #277). A day is a no-lesson day when its resolved schedule —
 * the date's `ScheduleAssignment`, else `defaultScheduleId` — is in
 * `noLessonScheduleIds` (daily schedules with "Lessons can be scheduled" off, e.g.
 * Half Day). The class still meets on those days; pacing just skips them.
 *
 * `meetingDates` is assumed ordered (as from {@link listClassMeetingDates}); order is
 * preserved in both outputs.
 */
export function splitLessonDays(
  meetingDates: string[],
  assignments: DateScheduleAssignment[],
  defaultScheduleId: string | null,
  noLessonScheduleIds: Set<string>
): { lessonDates: string[]; noLessonDays: NoLessonDay[] } {
  const assignmentByDate = new Map<string, string>();
  for (const a of assignments) assignmentByDate.set(a.date, a.dailyScheduleId);

  const lessonDates: string[] = [];
  const noLessonDays: NoLessonDay[] = [];
  for (const date of meetingDates) {
    const scheduleId = assignmentByDate.get(date) ?? defaultScheduleId;
    if (scheduleId != null && noLessonScheduleIds.has(scheduleId)) noLessonDays.push({ date, scheduleId });
    else lessonDates.push(date);
  }
  return { lessonDates, noLessonDays };
}

// ----------------------------------------------------------------------
// Pure pacing algorithm for the Curriculum → Pacing tab (Pacing Map epic).
//
// Places a course's lessons onto the real meeting days of a specific class,
// deliberately using only `targetPct` of the days and reserving the rest as
// buffer days at the END of each unit. Deterministic and side-effect free — no
// dates are read from the clock, nothing is persisted. All dates are ISO
// `YYYY-MM-DD` strings (lexicographically comparable).
// ----------------------------------------------------------------------

/** A curriculum unit as consumed by the algorithm. */
export type PacingUnit = {
  unitId: string;
  /** Order within the course. */
  sequence: number;
  /** ≈ weeks of instruction; used to weight day budgets. `null` → equal fallback. */
  approxDurationWeeks: number | null;
  /** Quarter this unit is assigned to, or `null` if the map leaves it unscheduled. */
  academicPeriodId: string | null;
};

/** A lesson within a unit. */
export type PacingLesson = {
  lessonId: string;
  /** Order within the unit. */
  sequence: number;
  /** Planned length; compared to `periodMinutes` for the overflow flag. `null` → ignored. */
  estimatedMinutes: number | null;
};

/**
 * A class's sparse override for one lesson (epic #123 `ClassLessonPlacement`).
 * Absence from the placements map = follow the computed baseline for that lesson.
 */
export type LessonPlacement = {
  /** Skip this lesson in this class — it consumes no content-day and doesn't render. */
  excluded?: boolean;
  /** Order within the unit for this class; overrides `lesson.sequence`. `null` = baseline. */
  sequenceOverride?: number | null;
  /** Pin to this ISO date; `null` = computed baseline date. Applied in Phase D. */
  scheduledDate?: string | null;
};

/** An academic period (quarter) — drives the "aligned" partition. */
export type PacingPeriod = {
  id: string;
  /** Inclusive ISO bounds. */
  startDate: string;
  endDate: string;
  sequence: number;
};

export type ComputePacingInput = {
  /** Ascending ISO dates the class actually meets (from Phase A `listClassMeetingDates`). */
  meetingDates: string[];
  units: PacingUnit[];
  /** Lessons grouped by `unitId`. Units with no entry are treated as having zero lessons. */
  lessonsByUnit: Record<string, PacingLesson[]>;
  academicPeriods: PacingPeriod[];
  /** Utilization target, e.g. 0.75. Clamped to [0, 1]. */
  targetPct: number;
  /** Class period length in minutes, for the overflow flag. `null` → no overflow flags. */
  periodMinutes: number | null;
  /**
   * Per-class pacing overrides keyed by `lessonId` (epic #123). Sparse — a lesson
   * absent from the map follows the computed baseline. `undefined` or an empty map
   * ⇒ output identical to the un-overridden schedule. Applied before apportionment:
   * excluded lessons are dropped; the rest are re-sequenced by `sequenceOverride`.
   */
  placements?: Map<string, LessonPlacement>;
  /**
   * Meeting days a lesson may NOT occupy (epic #277 — e.g. Half Days, whose daily
   * schedule has "Lessons can be scheduled" off). Callers already leave these out of
   * `meetingDates`; this set additionally voids any class pin (`scheduledDate`) that
   * lands on one — a stale pin, or one made before the schedule was switched off — so
   * the lesson reflows instead of being anchored to a day it can't use.
   */
  blockedDates?: Set<string>;
};

export type PacingUnitWindow = {
  unitId: string;
  /** First/last ISO date of the unit's window (content + trailing buffer), or `null` if it got no days. */
  startDate: string | null;
  endDate: string | null;
  contentDays: number;
  bufferDays: number;
  /** lessonId → the ISO date it was placed on. */
  lessonDates: Record<string, string>;
  /** Lessons that didn't fit this unit's content days (more lessons than days). */
  unplacedLessonIds: string[];
};

export type PacingResult = {
  mode: 'aligned' | 'sequential';
  summary: {
    /** Meeting days allocated to blocks (usableDays + bufferDays). */
    totalDays: number;
    usableDays: number;
    bufferDays: number;
    /** round(usableDays / totalDays × 100); 0 when there are no days. */
    utilizationPct: number;
    /** Total lessons that couldn't be placed (sum of per-unit unplaced). */
    overScheduledBy: number;
  };
  /** One window per input unit, in `sequence` order. */
  units: PacingUnitWindow[];
  /** Lessons whose `estimatedMinutes > periodMinutes` (period-fit flag only — never split). */
  overflowLessonIds: string[];
  /**
   * ISO dates carrying more than one placed lesson (epic #123 Phase D). Empty in
   * the common case: a date-pin reflows the unit's other lessons around it
   * (`reflowPins`), so dropping onto an occupied day anchors the dragged lesson
   * there and cascades that day's lesson and every later one forward (the vacated
   * day is left empty) instead of colliding. Only genuinely unresolvable pins
   * remain here — two pins on the same day, or a cross-unit pin landing in another
   * unit's window.
   */
  doubleBookedDates: string[];
};

// ----------------------------------------------------------------------

/**
 * Distribute `total` integer units across `weights` proportionally, summing
 * exactly to `total` (largest-remainder / Hamilton method). Ties broken by
 * index so the result is deterministic. Non-positive total weight → an even
 * split (also via largest remainder).
 */
function apportion(total: number, weights: number[]): number[] {
  const n = weights.length;
  if (n === 0 || total <= 0) return new Array(n).fill(0);

  const sumW = weights.reduce((a, b) => a + b, 0);
  const effective = sumW > 0 ? weights : new Array(n).fill(1);
  const effSum = sumW > 0 ? sumW : n;

  const exact = effective.map((w) => (w / effSum) * total);
  const result = exact.map(Math.floor);
  const distributed = result.reduce((a, b) => a + b, 0);
  let remaining = total - distributed;

  const byRemainder = exact
    .map((e, i) => ({ i, frac: e - Math.floor(e) }))
    .sort((a, b) => b.frac - a.frac || a.i - b.i);

  for (let k = 0; remaining > 0; k += 1, remaining -= 1) {
    result[byRemainder[k % n].i] += 1;
  }
  return result;
}

/** Per-unit weight: the unit's weeks, or the mean of the present weeks (1 if none). */
function unitWeights(units: PacingUnit[]): number[] {
  const present = units
    .map((u) => u.approxDurationWeeks)
    .filter((w): w is number => w != null && w > 0);
  const fallback = present.length ? present.reduce((a, b) => a + b, 0) / present.length : 1;
  return units.map((u) =>
    u.approxDurationWeeks != null && u.approxDurationWeeks > 0 ? u.approxDurationWeeks : fallback
  );
}

/** An empty window for a unit that received no schedulable days. */
function emptyWindow(unit: PacingUnit, lessons: PacingLesson[]): PacingUnitWindow {
  return {
    unitId: unit.unitId,
    startDate: null,
    endDate: null,
    contentDays: 0,
    bufferDays: 0,
    lessonDates: {},
    unplacedLessonIds: lessons.map((l) => l.lessonId),
  };
}

/**
 * Core budgeting for ONE block: a contiguous list of `blockDates` shared by
 * `blockUnits` (already in sequence order). Returns a window per unit. Every
 * date is consumed (content then buffer) in unit order, so windows are
 * non-overlapping with buffers at each unit's end.
 */
function placeBlock(
  blockDates: string[],
  blockUnits: PacingUnit[],
  lessonsByUnit: Record<string, PacingLesson[]>,
  targetPct: number
): PacingUnitWindow[] {
  const M = blockDates.length;
  if (blockUnits.length === 0) return [];
  if (M === 0) return blockUnits.map((u) => emptyWindow(u, lessonsByUnit[u.unitId] ?? []));

  const usable = Math.min(M, Math.max(0, Math.round(M * targetPct)));
  const buffer = M - usable;

  const weights = unitWeights(blockUnits);
  const contentBudget = apportion(usable, weights);
  const bufferBudget = apportion(buffer, weights);

  const windows: PacingUnitWindow[] = [];
  let cursor = 0;
  blockUnits.forEach((unit, i) => {
    const content = contentBudget[i];
    const buf = bufferBudget[i];
    const windowLen = content + buf;
    const lessons = (lessonsByUnit[unit.unitId] ?? [])
      .slice()
      .sort((a, b) => a.sequence - b.sequence);

    if (windowLen === 0) {
      windows.push(emptyWindow(unit, lessons));
      return;
    }

    const contentDates = blockDates.slice(cursor, cursor + content);
    const startDate = blockDates[cursor];
    const endDate = blockDates[cursor + windowLen - 1];
    cursor += windowLen;

    const lessonDates: Record<string, string> = {};
    const unplacedLessonIds: string[] = [];
    lessons.forEach((lesson, li) => {
      if (li < contentDates.length) lessonDates[lesson.lessonId] = contentDates[li];
      else unplacedLessonIds.push(lesson.lessonId);
    });

    windows.push({
      unitId: unit.unitId,
      startDate,
      endDate,
      contentDays: content,
      bufferDays: buf,
      lessonDates,
      unplacedLessonIds,
    });
  });

  return windows;
}

/** Meeting dates that fall within `[period.startDate, period.endDate]` inclusive. */
function datesInPeriod(meetingDates: string[], period: PacingPeriod): string[] {
  return meetingDates.filter((d) => d >= period.startDate && d <= period.endDate);
}

/**
 * Apply a class's sparse placements to the shared course lessons (epic #123):
 * drop `excluded` lessons and re-sequence the rest by `sequenceOverride`. Returns
 * the SAME object reference when there are no overrides, so the un-overridden path
 * is byte-for-byte identical to before. Downstream `placeBlock` sorts by `sequence`,
 * so the rewritten sequences flow through apportionment and placement unchanged.
 */
function applyPlacements(
  lessonsByUnit: Record<string, PacingLesson[]>,
  placements: Map<string, LessonPlacement> | undefined
): Record<string, PacingLesson[]> {
  if (!placements || placements.size === 0) return lessonsByUnit;

  const out: Record<string, PacingLesson[]> = {};
  for (const [unitId, lessons] of Object.entries(lessonsByUnit)) {
    const kept: PacingLesson[] = [];
    for (const lesson of lessons) {
      const p = placements.get(lesson.lessonId);
      if (p?.excluded) continue;
      kept.push(p?.sequenceOverride != null ? { ...lesson, sequence: p.sequenceOverride } : lesson);
    }
    out[unitId] = kept;
  }
  return out;
}

/**
 * Reflow manual date-pins (epic #123 Phase D) into the computed windows, in place.
 * A pinned lesson (`scheduledDate`) anchors to its exact date; the unit's remaining
 * lessons flow around it in sequence order via a single monotonic cursor over the
 * unit's meeting days — so dropping a lesson onto an occupied day pushes that day's
 * lesson and every LATER lesson FORWARD (a cascade / insert), not backward. The day
 * a lesson is dragged away from is intentionally left EMPTY (a free catch-up day);
 * lessons earlier than the anchor keep their days. Symmetric on a backward drag: the
 * dropped lesson lands exactly where dropped and the displaced lessons shift forward.
 * A pinned lesson that was unplaced is rescued onto its date.
 *
 * Only windows that OWN a pinned lesson are rewritten, so pin-free units stay
 * byte-for-byte identical to the computed placement (and their content/buffer day
 * budgets are untouched). Cascading forward can spill into trailing buffer days and,
 * once those run out, overflow the tail lesson to `unplacedLessonIds`. Genuinely
 * unresolvable pins still surface via `findDoubleBookedDates`: two pins on the same
 * date, or a cross-unit pin landing in another unit's window (that other window isn't
 * reflowed around a stray pin). No pins ⇒ no-op.
 */
function reflowPins(
  windows: PacingUnitWindow[],
  placements: Map<string, LessonPlacement> | undefined,
  lessonsByUnit: Record<string, PacingLesson[]>,
  meetingDates: string[]
): void {
  if (!placements || placements.size === 0) return;

  for (const w of windows) {
    const lessons = (lessonsByUnit[w.unitId] ?? []).slice().sort((a, b) => a.sequence - b.sequence);

    // Reflow only a window that actually owns a pinned lesson; leave the rest as
    // computed so pin-free units are unchanged.
    if (!lessons.some((l) => placements.get(l.lessonId)?.scheduledDate)) continue;

    // The unit's full meeting-day pool (content + trailing buffer), rebuilt from
    // the window's span — a contiguous run of `meetingDates`.
    const { startDate, endDate } = w;
    const pool =
      startDate && endDate ? meetingDates.filter((d) => d >= startDate && d <= endDate) : [];

    // Reserve every pin date that falls inside the pool so free lessons never land on
    // an anchored day.
    const reserved = new Set<string>();
    for (const l of lessons) {
      const pin = placements.get(l.lessonId)?.scheduledDate;
      if (pin && pool.includes(pin)) reserved.add(pin);
    }

    // Walk lessons in sequence order with a single monotonic cursor over the pool.
    // A pin anchors its lesson exactly and advances the cursor PAST the pin's day, so
    // days before it stay empty (the vacated slot) and later lessons cascade forward
    // into the days after it. Free lessons take the next un-reserved day at/after the
    // cursor; when the pool is exhausted they overflow to unplaced.
    const lessonDates: Record<string, string> = {};
    const unplacedLessonIds: string[] = [];
    let cursor = 0;
    for (const l of lessons) {
      const pin = placements.get(l.lessonId)?.scheduledDate;
      if (pin) {
        lessonDates[l.lessonId] = pin; // anchor exactly (rescued even if outside the pool)
        const pidx = pool.indexOf(pin);
        if (pidx >= 0) cursor = Math.max(cursor, pidx + 1);
      } else {
        while (cursor < pool.length && reserved.has(pool[cursor])) cursor += 1;
        if (cursor < pool.length) {
          lessonDates[l.lessonId] = pool[cursor];
          cursor += 1;
        } else {
          unplacedLessonIds.push(l.lessonId);
        }
      }
    }

    w.lessonDates = lessonDates;
    w.unplacedLessonIds = unplacedLessonIds;
  }
}

/** Drop pins on blocked (no-lesson) dates, keeping the lesson's other overrides. */
function voidBlockedPins(
  placements: Map<string, LessonPlacement> | undefined,
  blocked: Set<string> | undefined
): Map<string, LessonPlacement> | undefined {
  if (!placements || !blocked || blocked.size === 0) return placements;
  let changed = false;
  const out = new Map<string, LessonPlacement>();
  for (const [id, p] of placements) {
    if (p.scheduledDate && blocked.has(p.scheduledDate)) {
      out.set(id, { ...p, scheduledDate: null });
      changed = true;
    } else out.set(id, p);
  }
  return changed ? out : placements;
}

/** ISO dates that hold more than one placed lesson across all windows. */
function findDoubleBookedDates(windows: PacingUnitWindow[]): string[] {
  const counts = new Map<string, number>();
  for (const w of windows) {
    for (const date of Object.values(w.lessonDates)) {
      counts.set(date, (counts.get(date) ?? 0) + 1);
    }
  }
  return [...counts.entries()]
    .filter(([, n]) => n > 1)
    .map(([date]) => date)
    .sort();
}

// ----------------------------------------------------------------------

export function computePacing(input: ComputePacingInput): PacingResult {
  const { meetingDates, academicPeriods, periodMinutes } = input;
  const targetPct = Math.min(1, Math.max(0, input.targetPct));
  const units = input.units.slice().sort((a, b) => a.sequence - b.sequence);
  const placements = voidBlockedPins(input.placements, input.blockedDates);

  // Overlay this class's overrides (exclude + reorder) onto the shared lessons
  // before any placement. No overrides ⇒ the original map, so baseline is unchanged.
  const lessonsByUnit = applyPlacements(input.lessonsByUnit, placements);

  const mode: 'aligned' | 'sequential' =
    units.length > 0 && units.every((u) => u.academicPeriodId != null) ? 'aligned' : 'sequential';

  // Compute windows per block, then re-emit in the input unit order.
  const windowByUnit = new Map<string, PacingUnitWindow>();

  if (mode === 'sequential') {
    for (const w of placeBlock(meetingDates, units, lessonsByUnit, targetPct)) {
      windowByUnit.set(w.unitId, w);
    }
  } else {
    const periods = academicPeriods.slice().sort((a, b) => a.sequence - b.sequence);
    const assigned = new Set<string>();
    for (const period of periods) {
      const blockDates = datesInPeriod(meetingDates, period);
      const blockUnits = units.filter((u) => u.academicPeriodId === period.id);
      for (const w of placeBlock(blockDates, blockUnits, lessonsByUnit, targetPct)) {
        windowByUnit.set(w.unitId, w);
        assigned.add(w.unitId);
      }
    }
    // Units whose quarter isn't among academicPeriods are unplaceable.
    for (const u of units) {
      if (!assigned.has(u.unitId)) {
        windowByUnit.set(u.unitId, emptyWindow(u, lessonsByUnit[u.unitId] ?? []));
      }
    }
  }

  const orderedWindows = units.map((u) => windowByUnit.get(u.unitId)!);

  // Reflow manual date-pins into the computed placement (Phase D): each pin anchors
  // its lesson and the unit's others flow around it, then flag any day that still
  // carries more than one lesson. Both are no-ops when no lesson carries a pin.
  reflowPins(orderedWindows, placements, lessonsByUnit, meetingDates);
  const doubleBookedDates = findDoubleBookedDates(orderedWindows);

  const usableDays = orderedWindows.reduce((sum, w) => sum + w.contentDays, 0);
  const bufferDays = orderedWindows.reduce((sum, w) => sum + w.bufferDays, 0);
  const totalDays = usableDays + bufferDays;
  const overScheduledBy = orderedWindows.reduce((sum, w) => sum + w.unplacedLessonIds.length, 0);
  const utilizationPct = totalDays > 0 ? Math.round((usableDays / totalDays) * 100) : 0;

  const overflowLessonIds: string[] =
    periodMinutes == null
      ? []
      : Object.values(lessonsByUnit)
          .flat()
          .filter((l) => l.estimatedMinutes != null && l.estimatedMinutes > periodMinutes)
          .map((l) => l.lessonId);

  return {
    mode,
    summary: { totalDays, usableDays, bufferDays, utilizationPct, overScheduledBy },
    units: orderedWindows,
    overflowLessonIds,
    doubleBookedDates,
  };
}

// ======================================================================
// Class pacing assembly — the calendar → meeting days → computePacing pipeline,
// shared verbatim by the teacher app's `usePacingContext` and the agent's
// `computeClassPacing` so client and server dates can't drift apart.
// ======================================================================

/** A course's utilization target (%) when `Course.pacingTargetPct` is null (epic #266). */
export const DEFAULT_PACING_PCT = 75;

/** Parse an "HH:mm" 24h string into minutes-since-midnight; null if malformed. */
export function parseMinutes(t: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(t);
  if (!m) return null;
  return Number(m[1]) * 60 + Number(m[2]);
}

/** A bell-schedule period, as consumed by pacing. */
export type BellPeriod = { name: string; startTime: string; endTime: string };

/** Length (minutes) of a period, or null if either endpoint is malformed. */
export function periodLength(p: { startTime: string; endTime: string } | null): number | null {
  if (!p) return null;
  const start = parseMinutes(p.startTime);
  const end = parseMinutes(p.endTime);
  return start != null && end != null ? end - start : null;
}

/**
 * The modal (most common) positive period length on a daily schedule, or null if it
 * has none. Modal so a single block-day outlier period doesn't skew it.
 */
export function modalPeriodMinutes(periods: { startTime: string; endTime: string }[]): number | null {
  const counts = new Map<number, number>();
  for (const p of periods) {
    const len = periodLength(p);
    if (len != null && len > 0) counts.set(len, (counts.get(len) ?? 0) + 1);
  }
  let best: number | null = null;
  let bestCount = 0;
  for (const [len, count] of counts) {
    if (count > bestCount) {
      best = len;
      bestCount = count;
    }
  }
  return best;
}

/** A `ClassLessonPlacement` row (epic #123), as read from Data Connect. */
export type PlacementRow = {
  lessonPlan: { id: string };
  excluded: boolean;
  sequenceOverride?: number | null;
  scheduledDate?: string | null;
};

/** Key a class's placement rows by lessonId. */
export function placementsFromRows(rows: PlacementRow[]): Map<string, LessonPlacement> {
  const map = new Map<string, LessonPlacement>();
  for (const p of rows) {
    map.set(p.lessonPlan.id, {
      excluded: p.excluded,
      sequenceOverride: p.sequenceOverride,
      scheduledDate: p.scheduledDate,
    });
  }
  return map;
}

/** Everything `assemblePacing` reads. Field shapes mirror the Data Connect list queries. */
export type AssemblePacingInput = {
  schoolYear: { startDate: string; endDate: string } | null;
  specialDays: SpecialDayRange[];
  dailySchedules: { id: string; name: string; isDefault: boolean; allowsLessons: boolean }[];
  scheduleAssignments: { date: string; dailySchedule: { id: string } }[];
  units: {
    id: string;
    sequence: number;
    approxDurationWeeks?: number | null;
    academicPeriod?: { id: string } | null;
  }[];
  lessons: {
    id: string;
    sequence: number;
    status: string;
    estimatedMinutes?: number | null;
    unit: { id: string };
  }[];
  academicPeriods: PacingPeriod[];
  /** The class's sparse overlay (empty for a baseline). */
  placements: Map<string, LessonPlacement>;
  /** The daily schedules the scope meets on. */
  classScheduleIds: Set<string>;
  periodMinutes: number | null;
  /** 0–1. */
  targetPct: number;
};

export type AssembledPacing = {
  /** Meeting days a lesson can be placed on — the days pacing schedules into. */
  meetingDates: string[];
  /** Meeting days on a schedule with "Lessons can be scheduled" off (epic #277). */
  noLessonDays: { date: string; scheduleName: string }[];
  /** Lessons this class actually runs (non-ARCHIVED, net of exclusions). */
  lessonCount: number;
  /** Every non-ARCHIVED lesson keyed by unitId (INCLUDES class-excluded lessons). */
  lessonsByUnit: Map<string, PacingLesson[]>;
  pacing: PacingResult | null;
};

/**
 * Run the full pacing pipeline for one scope: instructional days → the days the
 * class meets → split off no-lesson days → `computePacing` with the class overlay.
 * Returns an empty result (`pacing: null`) when the school year isn't known.
 */
export function assemblePacing(input: AssemblePacingInput): AssembledPacing {
  const { schoolYear, placements } = input;
  if (!schoolYear)
    return { pacing: null, meetingDates: [], noLessonDays: [], lessonCount: 0, lessonsByUnit: new Map() };

  const defaultScheduleId = input.dailySchedules.find((d) => d.isDefault)?.id ?? null;

  const instructionalDates = listInstructionalDates(schoolYear.startDate, schoolYear.endDate, input.specialDays);
  const assignments = input.scheduleAssignments.map((a) => ({
    date: a.date,
    dailyScheduleId: a.dailySchedule.id,
  }));
  const allMeetingDates = listClassMeetingDates(
    instructionalDates,
    assignments,
    defaultScheduleId,
    input.classScheduleIds
  );
  // Epic #277: days on a schedule with "Lessons can be scheduled" off (e.g. Half Day)
  // are still meeting days, but pacing places nothing there — they're neither content
  // nor buffer. Only the lesson days feed computePacing.
  const noLessonScheduleIds = new Set(input.dailySchedules.filter((d) => !d.allowsLessons).map((d) => d.id));
  const scheduleName = new Map(input.dailySchedules.map((d) => [d.id, d.name]));
  const split = splitLessonDays(allMeetingDates, assignments, defaultScheduleId, noLessonScheduleIds);
  const meetingDates = split.lessonDates;
  const noLessonDays = split.noLessonDays.map((d) => ({
    date: d.date,
    scheduleName: scheduleName.get(d.scheduleId) ?? 'No-lesson day',
  }));

  const units = input.units.map((u) => ({
    unitId: u.id,
    sequence: u.sequence,
    approxDurationWeeks: u.approxDurationWeeks ?? null,
    academicPeriodId: u.academicPeriod?.id ?? null,
  }));

  const lessonsByUnit: Record<string, PacingLesson[]> = {};
  for (const l of input.lessons) {
    if (l.status === 'ARCHIVED') continue;
    (lessonsByUnit[l.unit.id] ??= []).push({
      lessonId: l.id,
      sequence: l.sequence,
      estimatedMinutes: l.estimatedMinutes ?? null,
    });
  }

  const academicPeriods = input.academicPeriods.map((p) => ({
    id: p.id,
    startDate: p.startDate,
    endDate: p.endDate,
    sequence: p.sequence,
  }));

  // Count only lessons this class actually runs (exclusions drop out).
  const lessonCount = Object.values(lessonsByUnit).reduce(
    (n, ls) => n + ls.filter((l) => !placements.get(l.lessonId)?.excluded).length,
    0
  );

  return {
    meetingDates,
    noLessonDays,
    lessonCount,
    lessonsByUnit: new Map(Object.entries(lessonsByUnit)),
    pacing: computePacing({
      meetingDates,
      units,
      lessonsByUnit,
      academicPeriods,
      targetPct: input.targetPct,
      periodMinutes: input.periodMinutes,
      placements,
      blockedDates: new Set(noLessonDays.map((d) => d.date)),
    }),
  };
}
